/**
 * Modal.
 *
 * Centered overlay for create flows and confirmations. Per UX guidelines:
 *   - Max-width 560px for forms, 480px for confirmations (caller picks via prop)
 *   - Esc closes, Enter submits primary (caller wires the form's onSubmit)
 *   - Backdrop click dismisses unless `lockBackdrop` is set (form-dirty case)
 *
 * Render-tree: portaled to <body> via React 19's `createPortal`. Scroll-locks
 * the body while open. Focus traps within the modal — the first focusable
 * element is auto-focused.
 */
import { createPortal } from 'react-dom';
import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  size?: ModalSize;
  lockBackdrop?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-[480px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  lockBackdrop = false,
  children,
  footer,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Esc handler + scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Focus the first focusable in the panel on open
    requestAnimationFrame(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(
        'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    });
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      // Mobile: no padding around the dialog so it fills the screen.
      // Desktop: backdrop padding gives a comfortable gutter at any size.
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/30 sm:items-center sm:p-4"
      onClick={lockBackdrop ? undefined : onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          // E19: max-h-[90vh] + flex column means the panel never overflows
          // the viewport; the header and footer stay pinned and the body
          // (via flex-1 min-h-0 + overflow-y-auto below) scrolls within.
          //
          // E28 mobile: on small viewports the panel slides up from the
          // bottom, fills width and most of the height, and drops the
          // border-radius / border for a native sheet feel. Desktop keeps
          // the bounded card look.
          'flex max-h-[95dvh] w-full flex-col bg-surface shadow-[var(--shadow-overlay)]',
          'sm:max-h-[90vh] sm:rounded-md sm:border sm:border-border-default',
          sizeClasses[size],
          className,
        )}
      >
        {(title || description) && (
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border-default px-5 py-4">
            <div className="min-w-0">
              {title ? <h2 className="text-base font-semibold text-primary">{title}</h2> : null}
              {description ? <p className="mt-0.5 text-sm text-secondary">{description}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-secondary"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border-default bg-base/40 px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
